import { jsPDF } from 'jspdf';
import dayjs from 'dayjs';
import logoUrl from '@/assets/images/logo.png';

/**
 * Branded invoice PDFs generated in-browser, so the admin can download an invoice to
 * forward to a partner/client without waiting on anything server-side. Mirrors the
 * plain Stripe/MyGigsters invoice: line amounts only, no GST line.
 *
 * Two entry points share one renderer:
 *  - {@link downloadJobInvoicePdf}   — a single completed B2B Partner Task.
 *  - {@link downloadBatchInvoicePdf} — a consolidated invoice from the Invoices
 *    page (one partner, many jobs), including its Sent/Paid status.
 */

/** Company identity on the invoice. ABN only renders when configured — we never
 *  print a fabricated tax number. Set REACT_APP_INVOICE_ABN to switch it on. */
const ISSUER = {
    name: 'Faastaa Pty Ltd',
    abn: process.env.REACT_APP_INVOICE_ABN || '',
    email: 'support@faastaa.com',
    site: 'www.faastaa.com',
};

const BRAND = '#052588';
const INK = '#1f2937';
const MUTED = '#6b7280';
const LINE = '#e5e7eb';
const PAID_GREEN = '#16a34a';

// ---------------------------------------------------------------------------
// Public input shapes
// ---------------------------------------------------------------------------

/** Minimal shape the single-job invoice needs; a superset of the list/detail JobRow. */
export interface InvoiceJob {
    businessAdminJobId: string;
    jobRefId?: string;
    businessAdminDisplayName?: string;
    branchName?: string;
    productName?: string;
    productDetails?: string;
    productCategory?: string;
    assemblyDate?: string;
    assemblyCost?: number;
    customerName?: string;
    customerNumber?: string;
    customerEmail?: string;
    addressLine1?: string;
    addressLine2?: string;
    suburb?: string;
    state?: string;
    acceptedTaskerName?: string;
    createTime?: string;
}

/** One job line inside a consolidated invoice (from the Invoices page). */
export interface BatchInvoiceJob {
    businessAdminJobId: string;
    jobRefId?: string;
    productName?: string;
    productCategory?: string;
    businessAdminInvoiceAmountCents?: number;
    completedTime?: string;
}

/** A consolidated invoice: one partner, one Stripe/MyGigsters invoice, many jobs. */
export interface BatchInvoiceInput {
    /** The real invoice id, or a synthetic "job-<id>" when a job is not yet invoiced. */
    invoiceId: string;
    businessAdminDisplayName?: string;
    /** Stripe/gateway payment status: open | paid | void | uncollectible | ... */
    paymentStatus?: string;
    /** When the invoice was sent to the partner. */
    sentTime?: string;
    /** When the partner paid it (present once paid). */
    paidTime?: string;
    jobs: BatchInvoiceJob[];
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

const money = (value: number) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Number.isFinite(value) ? value : 0);

const fmtDate = (value?: string) => {
    if (!value) return '-';
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD MMM YYYY') : value;
};

const centsToDollars = (cents?: number) => (Number.isFinite(cents as number) ? (cents as number) / 100 : 0);

/**
 * The partner is invoiced a percentage of the gross assembly price, not the price itself: a $249
 * job bills at $224.10. This mirrors the backend `business-admin.job.stripe.business-invoice-percent`
 * (default 90) — keep the two in sync, or override via REACT_APP_BUSINESS_INVOICE_PERCENT.
 */
const BUSINESS_INVOICE_PERCENT = Number(process.env.REACT_APP_BUSINESS_INVOICE_PERCENT) || 90;

/** Amount the partner is actually billed for a job: percent of the gross, rounded to cents like the
 *  backend (`round(assemblyCost * percent / 100)`), so this invoice matches the one they pay. */
function businessPayableAud(assemblyCost?: number): number {
    const gross = Number(assemblyCost) || 0;
    return Math.round(gross * BUSINESS_INVOICE_PERCENT) / 100;
}

/** Human, filesystem-safe reference used in invoice numbers and file names. */
function safeRef(raw?: string): string {
    return (raw || 'JOB').toString().replace(/[^A-Za-z0-9_-]+/g, '');
}

function joinAddress(job: InvoiceJob): string {
    const line1 = [job.addressLine1, job.addressLine2].filter(Boolean).join(', ');
    const line2 = [job.suburb, job.state].filter(Boolean).join(' ');
    return [line1, line2].filter(Boolean).join(', ') || '-';
}

function paymentLabel(status?: string): string {
    if (!status) return 'Unpaid';
    if (status === 'paid') return 'Paid';
    if (status === 'open') return 'Unpaid';
    if (status === 'void') return 'Void';
    if (status === 'uncollectible') return 'Uncollectible';
    return status;
}

/** Load the bundled logo as an <img> so jsPDF can embed it (webpack gives a URL). */
function loadLogo(): Promise<HTMLImageElement | null> {
    return new Promise(resolve => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = logoUrl;
    });
}

// ---------------------------------------------------------------------------
// Core renderer (shared by both entry points)
// ---------------------------------------------------------------------------

interface RenderLine {
    /** Bold first line (product / service name). */
    title: string;
    /** Muted supporting lines (category, ref, completed date, details, technician…). */
    subLines: string[];
    /** Amount in AUD. */
    amount: number;
}

interface RenderModel {
    invoiceNo: string;
    issuedText: string;
    paid: boolean;
    fromLines: string[];
    detailLines: string[];
    billToLines: string[];
    serviceForLines?: string[];
    lines: RenderLine[];
    notes: string;
    fileName: string;
}

async function renderAndSave(model: RenderModel): Promise<string> {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const marginX = 48;
    const contentW = pageW - marginX * 2;
    const amountColW = 110;
    const rowPadX = 12;
    const bottomLimit = pageH - 110; // keep clear of the footer band

    const heading = (text: string, x: number, yy: number) => {
        doc.setFont('helvetica', 'bold').setFontSize(9).setTextColor(MUTED);
        doc.text(text.toUpperCase(), x, yy);
    };
    const bodyBlock = (lines: string[], x: number, yy: number, width: number) => {
        doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(INK);
        let cursor = yy;
        for (const line of lines) {
            const wrapped = doc.splitTextToSize(line, width);
            doc.text(wrapped, x, cursor);
            cursor += wrapped.length * 13;
        }
        return cursor;
    };

    // ---- Header band -------------------------------------------------------
    const logo = await loadLogo();
    let headerBottom = 64;
    if (logo) {
        const logoW = 150;
        const logoH = (logo.height / logo.width) * logoW;
        doc.addImage(logo, 'PNG', marginX, 40, logoW, logoH);
        headerBottom = Math.max(headerBottom, 40 + logoH);
    } else {
        doc.setFont('helvetica', 'bold').setFontSize(22).setTextColor(BRAND);
        doc.text('Faastaa', marginX, 62);
    }

    doc.setFont('helvetica', 'bold').setFontSize(20).setTextColor(BRAND);
    doc.text('INVOICE', pageW - marginX, 56, { align: 'right' });
    doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(MUTED);
    doc.text(`Invoice #: ${model.invoiceNo}`, pageW - marginX, 74, { align: 'right' });
    doc.text(model.issuedText, pageW - marginX, 88, { align: 'right' });

    if (model.paid) {
        // Green "PAID" pill under the date, right-aligned.
        const label = 'PAID';
        doc.setFont('helvetica', 'bold').setFontSize(10);
        const textW = doc.getTextWidth(label);
        const pillW = textW + 20;
        const pillX = pageW - marginX - pillW;
        doc.setFillColor(PAID_GREEN);
        doc.roundedRect(pillX, 96, pillW, 18, 4, 4, 'F');
        doc.setTextColor('#ffffff');
        doc.text(label, pillX + pillW / 2, 108.5, { align: 'center' });
        headerBottom = Math.max(headerBottom, 114);
    }

    let y = Math.max(headerBottom, 96) + 18;
    doc.setDrawColor(BRAND).setLineWidth(2);
    doc.line(marginX, y, pageW - marginX, y);
    y += 24;

    // ---- Parties -----------------------------------------------------------
    const colW = contentW / 2;
    const leftX = marginX;
    const rightX = marginX + colW + 12;

    heading('From', leftX, y);
    const fromBottom = bodyBlock(model.fromLines, leftX, y + 16, colW - 12);
    heading('Invoice details', rightX, y);
    const detailBottom = bodyBlock(model.detailLines, rightX, y + 16, colW - 12);
    y = Math.max(fromBottom, detailBottom) + 16;

    heading('Bill to', leftX, y);
    const billBottom = bodyBlock(model.billToLines, leftX, y + 16, colW - 12);
    let serviceBottom = y;
    if (model.serviceForLines && model.serviceForLines.length) {
        heading('Service performed for', rightX, y);
        serviceBottom = bodyBlock(model.serviceForLines, rightX, y + 16, colW - 12);
    }
    y = Math.max(billBottom, serviceBottom) + 22;

    // ---- Line-items table (with page breaks) -------------------------------
    const drawTableHeader = (yy: number) => {
        doc.setFillColor(BRAND);
        doc.rect(marginX, yy, contentW, 26, 'F');
        doc.setFont('helvetica', 'bold').setFontSize(10).setTextColor('#ffffff');
        doc.text('Description', leftX + rowPadX, yy + 17);
        doc.text('Amount (AUD)', pageW - marginX - rowPadX, yy + 17, { align: 'right' });
        return yy + 26;
    };
    y = drawTableHeader(y);

    const descColW = contentW - amountColW;
    let lineTop = y;
    for (const item of model.lines) {
        // Measure the row height first so we can page-break cleanly.
        doc.setFont('helvetica', 'bold').setFontSize(10);
        const titleWrapped = doc.splitTextToSize(item.title, descColW - rowPadX * 2);
        let rowH = 8 + titleWrapped.length * 13;
        const subWrapped: string[][] = [];
        doc.setFont('helvetica', 'normal').setFontSize(9);
        for (const s of item.subLines) {
            const w = doc.splitTextToSize(s, descColW - rowPadX * 2);
            subWrapped.push(w);
            rowH += w.length * 12;
        }
        rowH += 10;

        if (lineTop + rowH > bottomLimit) {
            doc.addPage();
            lineTop = 56;
            lineTop = drawTableHeader(lineTop);
        }

        let cellY = lineTop + 18;
        doc.setFont('helvetica', 'bold').setFontSize(10).setTextColor(INK);
        doc.text(titleWrapped, leftX + rowPadX, cellY);
        cellY += titleWrapped.length * 13;
        doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(MUTED);
        for (const w of subWrapped) {
            doc.text(w, leftX + rowPadX, cellY);
            cellY += w.length * 12;
        }
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(INK);
        doc.text(money(item.amount), pageW - marginX - rowPadX, lineTop + 18, { align: 'right' });

        const rowBottom = Math.max(cellY, lineTop + 18) + 10;
        doc.setDrawColor(LINE).setLineWidth(1);
        doc.line(marginX, rowBottom, pageW - marginX, rowBottom);
        doc.line(pageW - marginX - amountColW, lineTop, pageW - marginX - amountColW, rowBottom);
        lineTop = rowBottom;
    }
    y = lineTop;

    // ---- Totals (no GST line — mirrors the Stripe/MyGigsters invoice: Subtotal / Total / due) ----
    const total = model.lines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);

    if (y + 90 > bottomLimit) {
        doc.addPage();
        y = 56;
    }
    const totalsX = pageW - marginX - 220;
    const totalRow = (label: string, value: string, bold = false) => {
        doc.setFont('helvetica', bold ? 'bold' : 'normal').setFontSize(bold ? 12 : 10);
        doc.setTextColor(bold ? BRAND : INK);
        doc.text(label, totalsX, y + 14);
        doc.text(value, pageW - marginX, y + 14, { align: 'right' });
        y += bold ? 22 : 18;
    };
    y += 8;
    totalRow('Subtotal', money(total));
    totalRow('Total', money(total));
    doc.setDrawColor(LINE).setLineWidth(1);
    doc.line(totalsX, y + 2, pageW - marginX, y + 2);
    y += 6;
    totalRow(model.paid ? 'Total paid' : 'Amount due', money(total), true);

    // ---- Footer (last page) ------------------------------------------------
    let fy = pageH - 96;
    doc.setDrawColor(LINE).setLineWidth(1);
    doc.line(marginX, fy, pageW - marginX, fy);
    fy += 18;
    doc.setFont('helvetica', 'bold').setFontSize(9).setTextColor(MUTED);
    doc.text('NOTES', marginX, fy);
    fy += 14;
    doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(INK);
    doc.text(doc.splitTextToSize(model.notes, contentW), marginX, fy);

    doc.save(model.fileName);
    return model.fileName;
}

// ---------------------------------------------------------------------------
// Entry point 1 — single completed job
// ---------------------------------------------------------------------------

export async function downloadJobInvoicePdf(job: InvoiceJob): Promise<string> {
    const ref = safeRef(job.jobRefId?.trim() || job.businessAdminJobId);

    const meta: string[] = [];
    if (job.productCategory) meta.push(job.productCategory);
    if (job.jobRefId?.trim()) meta.push(`Ref ${job.jobRefId.trim()}`);
    meta.push(`Completed ${fmtDate(job.assemblyDate)}`);
    const subLines: string[] = [meta.join('  •  ')];
    if (job.productDetails?.trim()) subLines.push(job.productDetails.trim());
    if (job.acceptedTaskerName) subLines.push(`Technician: ${job.acceptedTaskerName}`);

    const fromLines = [ISSUER.name];
    if (ISSUER.abn) fromLines.push(`ABN: ${ISSUER.abn}`);
    fromLines.push(ISSUER.email, ISSUER.site);

    const serviceForLines = [job.customerName || '-'];
    if (job.customerNumber) serviceForLines.push(job.customerNumber);
    if (job.customerEmail) serviceForLines.push(job.customerEmail);
    serviceForLines.push(joinAddress(job));

    return renderAndSave({
        invoiceNo: `INV-${ref}`,
        issuedText: `Date issued: ${dayjs().format('DD MMM YYYY')}`,
        paid: false,
        fromLines,
        detailLines: [
            `Partner reference: ${job.jobRefId?.trim() || '-'}`,
            `Faastaa job ID: ${job.businessAdminJobId}`,
            `Service date: ${fmtDate(job.assemblyDate)}`,
        ],
        billToLines: [
            job.businessAdminDisplayName || 'Business partner',
            ...(job.branchName ? [`Branch: ${job.branchName}`] : []),
        ],
        serviceForLines,
        lines: [
            {
                title: job.productName || 'Assembly / installation service',
                subLines,
                amount: businessPayableAud(job.assemblyCost),
            },
        ],
        notes: 'This invoice covers the completed B2B partner task above. Amounts are in AUD. Thank you for partnering with Faastaa.',
        fileName: `Faastaa-invoice-${ref}.pdf`,
    });
}

// ---------------------------------------------------------------------------
// Entry point 2 — consolidated (multi-job) invoice from the Invoices page
// ---------------------------------------------------------------------------

export async function downloadBatchInvoicePdf(input: BatchInvoiceInput): Promise<string> {
    const isReal = !!input.invoiceId && !input.invoiceId.startsWith('job-');
    const paid = (input.paymentStatus || '').toLowerCase() === 'paid';

    // A stable, readable reference for the number + file name.
    const firstJobRef = input.jobs[0]?.jobRefId?.trim() || input.jobs[0]?.businessAdminJobId;
    const invoiceNo = isReal ? input.invoiceId : `INV-${safeRef(firstJobRef)}`;
    const fileRef = safeRef(isReal ? input.invoiceId : firstJobRef);

    const fromLines = [ISSUER.name];
    if (ISSUER.abn) fromLines.push(`ABN: ${ISSUER.abn}`);
    fromLines.push(ISSUER.email, ISSUER.site);

    const detailLines: string[] = [];
    detailLines.push(`Invoice ID: ${isReal ? input.invoiceId : 'Pending invoice'}`);
    detailLines.push(`Jobs: ${input.jobs.length}`);
    if (input.sentTime) detailLines.push(`Sent: ${fmtDate(input.sentTime)}`);
    detailLines.push(`Payment: ${paymentLabel(input.paymentStatus)}`);
    if (paid && input.paidTime) detailLines.push(`Paid on: ${fmtDate(input.paidTime)}`);

    const lines: RenderLine[] = input.jobs.map(job => {
        const meta: string[] = [];
        if (job.productCategory) meta.push(job.productCategory);
        meta.push(`Ref ${job.jobRefId?.trim() || job.businessAdminJobId}`);
        if (job.completedTime) meta.push(`Completed ${fmtDate(job.completedTime)}`);
        return {
            title: job.productName || 'Assembly / installation service',
            subLines: [meta.join('  •  ')],
            amount: centsToDollars(job.businessAdminInvoiceAmountCents),
        };
    });

    const issuedText = input.sentTime
        ? `Date issued: ${fmtDate(input.sentTime)}`
        : `Date issued: ${dayjs().format('DD MMM YYYY')}`;

    return renderAndSave({
        invoiceNo,
        issuedText,
        paid,
        fromLines,
        detailLines,
        billToLines: [input.businessAdminDisplayName || 'Business partner'],
        lines,
        notes: paid
            ? 'This invoice has been paid in full. Amounts are in AUD. Thank you for partnering with Faastaa.'
            : 'This consolidated invoice covers the completed B2B partner tasks above. Amounts are in AUD. Thank you for partnering with Faastaa.',
        fileName: `Faastaa-invoice-${fileRef}.pdf`,
    });
}
